import React, { useState, useEffect, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import ChatInterface from "./pages/ChatInterface";
import Login from "./pages/Login";
import LogoManagement from "./pages/LogoManagement";
import DocumentUpload from "./pages/DocumentUpload";
import WidgetIntegration from "./pages/WidgetIntegration";
import DomainManagement from "./pages/DomainManagement";
import PromptVersioning from "./pages/PromptVersioning";
import ModelManagement from "./pages/ModelManagement";
import ApiToolsManagement from "./pages/ApiToolsManagement";
import WebhookManagement from "./pages/WebhookManagement";
import Integrations from "./pages/Integrations";
import FreshchatConfig from "./pages/FreshchatConfig";
import TelegramConfig from "./pages/TelegramConfig";
import SlackConfig from "./pages/SlackConfig";
import EmailConfig from "./pages/EmailConfig";
import KnowledgeBase from "./pages/KnowledgeBase";
import Documentation from "./pages/Documentation";
import WidgetPreview from "./pages/WidgetPreview";
import WidgetPreviewDisplay from "./pages/WidgetPreviewDisplay";
import Agents from "./pages/Agents";
import Analytics from "./pages/Analytics";
import CampaignManagement from "./pages/CampaignManagement";
import BusinessFlows from "./pages/BusinessFlows";
import UserManagement from "./pages/UserManagement";
import Contacts from "./pages/Contacts";
import AgentChatInterface from "./pages/AgentChatInterface";
import AdminDashboard from "./pages/AdminDashboard";
import PaymentPage from "./components/payment/PaymentPage";
import api from "./utils/api";

// Global font styles
const globalStyles = `
  body {
    font-family: 'Proxima Nova', 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }
`;

if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = globalStyles;
  document.head.appendChild(styleElement);
}

/* ---------------------------------------------------------
   FIXED — Moved AdminPanelWrapper OUTSIDE AppContent
--------------------------------------------------------- */
function AdminPanelWrapper({ onLogout, user, activeAgentId, agents }) {
  const navigate = useNavigate();

  const handleAdminPanel = () => {
    navigate("/admin");
  };

  return (
    <ChatInterface
      fullscreen={true}
      onLogout={onLogout}
      onAdminPanel={handleAdminPanel}
      user={user}
      activeAgentId={activeAgentId}
      agents={agents}
    />
  );
}

/* ---------------------------------------------------------
   MAIN APP CONTENT
--------------------------------------------------------- */

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("logo");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeAgentId, setActiveAgentId] = useState(() => {
    const saved = localStorage.getItem("activeAgentId");
    return saved || "1";
  });

  const [agents, setAgents] = useState([]);

  const [activeChatsCount, setActiveChatsCount] = useState(0);

  const [user, setUser] = useState(() => {
    const saved = sessionStorage.getItem("user");
    return saved ? JSON.parse(saved) : null;
  });

  const handleLogin = () => {
    const saved = sessionStorage.getItem("user");
    const userData = saved ? JSON.parse(saved) : { authenticated: true };
    setUser(userData);
    
    if (userData.role === 'agent') {
      const assigned = userData.assigned_agents || [];
      const firstBot = assigned.length > 0 ? String(assigned[0]) : String(userData.agent_id || "1");
      setActiveAgentId(firstBot);
      localStorage.setItem("activeAgentId", firstBot);
    } else if (userData.agent_id) {
      setActiveAgentId(String(userData.agent_id));
      localStorage.setItem("activeAgentId", userData.agent_id);
    }
    
    // Auto-redirect based on role
    if (userData.role === 'agent') {
      navigate('/agent-chat');
    } else if (userData.role === 'user') {
      navigate('/chat');
    } else {
      navigate('/admin');
    }
  };

  // Enforce STRICT redirection: Agents/Users are ONLY allowed in their respective interfaces
  useEffect(() => {
    if (user && user.role !== 'admin') {
      const isAgent = user.role === 'agent';
      const isUser = user.role === 'user';
      
      // Whitelist of allowed paths
      const allowedPaths = isAgent 
        ? ['/agent-chat', '/login']
        : isUser 
          ? ['/chat', '/login']
          : ['/login'];

      const isAllowed = allowedPaths.some(path => 
        location.pathname === path || location.pathname.startsWith(path + '/')
      );

      if (!isAllowed) {
        console.warn(`🚫 RBAC: Redirecting ${user.role} from unauthorized path: ${location.pathname}`);
        navigate(isAgent ? '/agent-chat' : isUser ? '/chat' : '/login');
      }
    }
  }, [user, navigate, location.pathname]);

  const handleLogout = () => {
    setUser(null);
    // Nuke ALL stored state so nothing leaks between accounts
    localStorage.clear();
    sessionStorage.clear();
    navigate('/login');
  };

  const fetchAgents = useCallback(async () => {
    try {
      const data = await api.request("/agents");
      if (data && data.agents) {
        setAgents(data.agents);
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  }, []); // Decoupled from activeAgentId to prevent reload loops

  const fetchActiveChatsCount = useCallback(async () => {
    try {
      const data = await api.request("/analytics/active-chats");
      if (data && typeof data.active_sessions === "number") {
        setActiveChatsCount(data.active_sessions);
      }
    } catch (error) {
      console.error("Error fetching active chats count:", error);
    }
  }, []);

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchAgents();
    }
  }, [fetchAgents, user]);

  // Handle Agent Validation Separately
  useEffect(() => {
    if (agents.length > 0) {
      // Robust multi-role agent restriction:
      // 1. Admins see all agents.
      // 2. Regular client users are strictly locked to their database-assigned agent_id.
      // 3. Agents are restricted to their assigned_agents list (or default agent_id).
      let assignedIds = [];
      if (user) {
        if (user.role === 'admin') {
          assignedIds = agents.map(p => String(p.id));
        } else if (user.role === 'user') {
          assignedIds = user.agent_id ? [String(user.agent_id)] : (agents.length > 0 ? [String(agents[0].id)] : []);
        } else {
          assignedIds = (user.assigned_agents && user.assigned_agents.length > 0)
            ? user.assigned_agents.map(id => String(id))
            : (user.agent_id ? [String(user.agent_id)] : [String(agents[0].id)]);
        }
      } else {
        assignedIds = agents.map(p => String(p.id));
      }

      const isCurrentValid = assignedIds.includes(String(activeAgentId));

      if (!isCurrentValid) {
        const defaultId = assignedIds.length > 0 ? assignedIds[0] : agents[0].id;
        console.log(`RBAC: Resetting active agent to authorized default: ${defaultId}`);
        setActiveAgentId(defaultId);
      }
    }
  }, [agents, activeAgentId, user]);

  useEffect(() => {
    if (activeAgentId) {
      api.setAgentId(activeAgentId);
    }
  }, [activeAgentId]);

  useEffect(() => {
    if (user && activeAgentId) {
      // Initial fetch
      fetchActiveChatsCount();
      
      // Poll every 30 seconds for live updates
      const interval = setInterval(() => {
        fetchActiveChatsCount();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [fetchActiveChatsCount, user, activeAgentId]);

  useEffect(() => {
    localStorage.setItem("activeAgentId", activeAgentId);
    api.setAgentId(activeAgentId);
  }, [activeAgentId]);

  return (
    <Routes key={activeAgentId}>
      {/* Public routes */}

      <Route path="/login" element={
        !user ? (
          <Login onLogin={handleLogin} />
        ) : (
          <Navigate to={user.role === 'agent' ? '/agent-chat' : user.role === 'user' ? '/chat' : '/admin'} replace />
        )
      } />

      {!user ? (
        <Route path="/*" element={<Navigate to="/login" replace />} />
      ) : (
        <>
          <Route path="/" element={<Navigate to="/admin" replace />} />

          <Route
            path="/chat"
            element={
              <AdminPanelWrapper 
                onLogout={handleLogout} 
                user={user} 
                activeAgentId={activeAgentId}
                agents={agents}
              />
            }
          />

          {/* Admin Dashboard */}
          <Route
            path="/admin"
            element={
              <div className="flex h-screen bg-gray-50">
                <Sidebar
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  isCollapsed={sidebarCollapsed}
                  setIsCollapsed={setSidebarCollapsed}
                  activeChatsCount={activeChatsCount}
                />
                <div className="flex-1 flex flex-col">
                  <Navbar
                    onToggleSidebar={() =>
                      setSidebarCollapsed(!sidebarCollapsed)
                    }
                    sidebarCollapsed={sidebarCollapsed}
                    activeAgentId={activeAgentId}
                    setActiveAgentId={setActiveAgentId}
                    agents={agents}
                    activeChatsCount={activeChatsCount}
                  />
                  <main className="flex-1 overflow-y-auto custom-scrollbar bg-gray-50">
                    <AdminDashboard
                      activeAgentId={activeAgentId}
                      agents={agents}
                      setActiveTab={setActiveTab}
                      fetchAgents={fetchAgents}
                      setActiveAgentId={setActiveAgentId}
                      activeChatsCount={activeChatsCount}
                    />
                  </main>
                </div>
              </div>
            }
          />

          {/* Logo */}
          <Route
            path="/logo"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<LogoManagement />}
                wide={true}
              />
            }
          />

          {/* Smart FAQ */}
          <Route
            path="/smart-faq"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<KnowledgeBase />}
                wide={true}
              />
            }
          />

          {/* Documents */}
          <Route
            path="/documents"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<DocumentUpload />}
                wide={true}
              />
            }
          />

          {/* Widget */}
          <Route
            path="/widget"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<WidgetIntegration />}
              />
            }
          />

          {/* Widget Preview */}
          <Route
            path="/widget-preview"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<WidgetPreview />}
              />
            }
          />

          {/* Widget Preview Display - Fullscreen without sidebar */}
          <Route
            path="/widget-preview/display"
            element={<WidgetPreviewDisplay />}
          />

          {/* Instructions */}
          <Route
            path="/instructions"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<PromptVersioning />}
                wide={true}
              />
            }
          />

          {/* Models */}
          <Route
            path="/models"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<ModelManagement />}
                wide={true}
              />
            }
          />

          {/* Agents */}
          <Route
            path="/agents"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<Agents />}
                wide={true}
              />
            }
          />



          {/* API Tools */}
          <Route
            path="/api-tools"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<ApiToolsManagement />}
                wide={true}
              />
            }
          />

          {/* Help Center */}
          <Route
            path="/docs"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<Documentation />}
              />
            }
          />

          {/* Campaigns */}
          <Route
            path="/campaigns"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<CampaignManagement />}
                wide={true}
              />
            }
          />

          {/* Business Flows */}
          <Route
            path="/business-flows"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<BusinessFlows />}
                wide={true}
              />
            }
          />

          {/* Webhooks */}
          <Route
            path="/webhooks"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<WebhookManagement />}
                wide={true}
              />
            }
          />

          {/* Domains */}
          <Route
            path="/domains"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<DomainManagement />}
                wide={true}
              />
            }
          />

          {/* Integrations */}
          <Route
            path="/integrations"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<Integrations />}
                wide={true}
              />
            }
          />

          {/* Freshchat Config */}
          <Route
            path="/freshchat-config"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<FreshchatConfig />}
              />
            }
          />

          {/* Telegram Config */}
          <Route
            path="/telegram-config"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<TelegramConfig />}
              />
            }
          />

          {/* Slack Config */}
          <Route
            path="/slack-config"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<SlackConfig />}
              />
            }
          />

          {/* Email Config */}
          <Route
            path="/email-config"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<EmailConfig />}
              />
            }
          />


          {/* Tenant Agents */}
          <Route
            path="/agents"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<Agents />}
              />
            }
          />

          <Route
            path="/analytics"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<Analytics />}
                wide={true}
              />
            }
          />

          <Route
            path="/user-management"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<UserManagement />}
                wide={true}
              />
            }
          />

          <Route
            path="/billing"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<PaymentPage userName={user?.username || "admin"} tenantId={user?.tenant_id} />}
                wide={true}
              />
            }
          />

          <Route
            path="/contacts"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<Contacts />}
                wide={true}
              />
            }
          />

          <Route
            path="/agent-chat"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAgentId={activeAgentId}
                setActiveAgentId={setActiveAgentId}
                agents={agents}
                fetchAgents={fetchAgents}
                activeChatsCount={activeChatsCount}
                content={<AgentChatInterface onLogout={handleLogout} user={user} hideHeader={true} />}
                wide={true}
              />
            }
          />
        </>
      )}
    </Routes>
  );
}

/* ---------------------------------------------------------
   REUSABLE PAGE WRAPPER
--------------------------------------------------------- */
function PageWrapper({
  activeTab,
  setActiveTab,
  sidebarCollapsed,
  setSidebarCollapsed,
  activeAgentId,
  setActiveAgentId,
  agents,
  fetchAgents,
  content,
  activeChatsCount,
  wide = false,
}) {
  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isCollapsed={sidebarCollapsed}
        setIsCollapsed={setSidebarCollapsed}
        activeChatsCount={activeChatsCount}
      />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar
          onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
          sidebarCollapsed={sidebarCollapsed}
          activeAgentId={activeAgentId}
          setActiveAgentId={setActiveAgentId}
          agents={agents}
          activeChatsCount={activeChatsCount}
        />
        <main className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar bg-white min-w-0">
          <div className={`${wide ? "p-0 max-w-full" : "p-8 max-w-5xl mx-auto"}`}>
            {React.cloneElement(content, { activeAgentId, fetchAgents, agents })}
          </div>
        </main>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------
   APP ROOT
--------------------------------------------------------- */
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
